import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import moment from "jalali-moment";
import { Button, Spin } from "antd";
import { getSowingDateReport } from "@services/api";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import ViolinPlot from "@/components/reports/sowing-date-recommendation/ViolinPlot";
import GanttChart from "@/components/reports/sowing-date-recommendation/GanttChart";
import WeatherChart from "@/components/reports/sowing-date-recommendation/WeatherChart";
import ScatterPlotChart from "@/components/reports/sowing-date-recommendation/ScatterPlotChart";
import {
  calculateAverage,
  convertDecadesViolinText,
} from "@helpers/chartHelper";
import { hejriDate } from "@helpers/financial_helper";
import AgroMapFarm from "@/features/map/presentation/components/AgroMapFarm";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import sowingLogo from "@assets/images/sowing_date_recommendation/sowingLogo.png";
import frame2182 from "@assets/images/sowing_date_recommendation/Frame 2182.png";
import line from "@assets/images/harvest_date_recommendation_images/Line185.png";
import headerImage from "@assets/images/harvest_date_recommendation_images/headerImage.png";
import footerImage from "@assets/images/harvest_date_recommendation_images/footerImage.png";
import footerTitle from "@assets/images/harvest_date_recommendation_images/footerTitle.png";
import sowingDateReport from "@assets/images/sowing_date_recommendation/sowingDateReport.png";
import agroyaarPlatform from "@assets/images/harvest_date_recommendation_images/agroyaarPlatform.png";

function SowingDateRecommendationReport() {
  const { id } = useParams();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndtDate] = useState();
  const [maxAvg, setMaxAvg] = useState();
  const [ranges, setRanges] = useState([]);
  const [decades, setDecades] = useState([]);
  // const currentGeorgianYear = moment().year();
  // const year = currentGeorgianYear; // سال میلادی مورد نظر

  // دیتای کل نمودارها و گزارش
  const sowingDateReportQuery = useQuery({
    queryKey: ["sowingDateId", id],
    queryFn: () => getSowingDateReport(id),
    refetchInterval: (data) => (data?.sowingDateRecommendation ? false : 20000),
  });

  // دیتاهای هواشناسی گذشته
  const minTemperatureHistory =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.climaticHistoryWeatherData?.map(
      (minTemp) => {
        return minTemp.minTemperature;
      }
    );

  const maxTemperatureHistory =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.climaticHistoryWeatherData?.map(
      (maxTemp) => {
        return maxTemp.maxTemperature;
      }
    );

  const precipitationHistory =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.climaticHistoryWeatherData?.map(
      (prcpn) => {
        return prcpn.precipitation;
      }
    );

  const performancePotential =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.sowingDateRangeForecasts?.map(
      (potentiel) => parseInt(potentiel.performancePotential)
    );

  // دیتاهای هواشناسی آینده
  const minTemperatureFuture =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.futurePredictionWeatherData?.map(
      (minTemp) => {
        return minTemp.minTemperature;
      }
    );

  const maxTemperatureFuture =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.futurePredictionWeatherData?.map(
      (maxTemp) => {
        return maxTemp.maxTemperature;
      }
    );

  const precipitationFuture =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.futurePredictionWeatherData?.map(
      (prcpn) => {
        return prcpn.precipitation;
      }
    );

  const datesPotentail =
    sowingDateReportQuery?.data?.sowingDateRecommendation?.sowingDateRangeForecasts?.map(
      (sowingDayOfYear) => {
        return moment(`${moment(sowingDateReportQuery?.data?.suggestedSowingDate).year()}-01-01`, "YYYY-MM-DD")
          .add(sowingDayOfYear.sowingDayOfYear, "days")
          .toDate()
          .toLocaleDateString("fa-IR");
      }
    );

  //  میانگین پتانسیل هر روز با 10 روز آیندش و بدست آوردن بالاترین میانگین و نمایش تاریخ اون بازه ---------
  let averagesArray = [];

  const averages = useMemo(() => {
    if (performancePotential) {
      let performancePotentialArray = [...performancePotential];
      let start = 0;
      let end = 10;

      const calculateAverage = function () {
        if (averagesArray.length < 50) {
          let firstTen = performancePotentialArray.slice(start, end);
          const average = +(
            firstTen.reduce((total, value) => total + value, 0) / 10
          ).toFixed(0);
          start = start + 1;
          end = end + 1;
          averagesArray.push(average);
          calculateAverage();
        } else {
          return;
        }
      };

      calculateAverage();

      return averagesArray;
    }
  }, [performancePotential]);

  useEffect(() => {
    if (averages?.length > 0) {
      setMaxAvg(Math.max(...averagesArray));
      const day = averagesArray?.findIndex((avg) => maxAvg === avg);
      setStartDate(datesPotentail[day]);
      setEndtDate(datesPotentail[day + 10]);
    }
  }, [averages]);

  // ------------------------------

  // مجموع بارش سال گذشته
  const totalPrecipitationHistory = precipitationHistory?.reduce(
    (total, value) => total + value,
    0
  );

  // مجموع بارش سال آینده
  const totalPrecipitationFuture = precipitationFuture?.reduce(
    (total, value) => total + value,
    0
  );

  // تاریخ کاشت و برداشت و اختلاف روز بین آنها
  const dateStart = moment(ranges[0]?.Start);
  const dateEnd = moment(ranges[0]?.End);
  const diffInDays = dateEnd?.diff(dateStart, "days");

  // تشکیل اون آرایه 60 تایی و دسته بندی آنها به 6 تا 10 تایی برای نمودار ها
  useEffect(() => {
    let sixDecades = [];
    let decade = [];

    if (
      sowingDateReportQuery?.data?.sowingDateRecommendation
        ?.sowingDateRangeForecasts
    ) {
      sixDecades =
        sowingDateReportQuery?.data?.sowingDateRecommendation?.sowingDateRangeForecasts?.reduce(
          (decades, item) => {
            decade.push(item);
            if (decade.length === 10) {
              decades.push(decade);
              decade = [];
            }
            return decades;
          },
          []
        );
      setDecades(sixDecades);
    }
  }, [
    sowingDateReportQuery?.data?.sowingDateRecommendation
      ?.sowingDateRangeForecasts,
  ]);

  // تبدیل و فرمت دهه ها و تاریخ ها
  const firstIndex = decades?.map((decade) => decade[0].sowingDayOfYear);

  const getDateFromDayOfYear = (day, year) => {
    const startDate = new Date(year, 0, 1); // تاریخ 1 ژانویه سال مشخص شده
    const resultDate = new Date(startDate);
    resultDate.setDate(startDate.getDate() + day - 1); // تعداد روزهای گذشته از 1 ژانویه را اضافه می‌کند
    return hejriDate(resultDate.toISOString().split("T")[0]); // تاریخ را به فرمت YYYY-MM-DD تبدیل می‌کند
  };

  const dates = firstIndex.map((day) => getDateFromDayOfYear(day, moment(sowingDateReportQuery?.data?.suggestedSowingDate).year()));

  let firstDate = dates[0];

  // محاسبه حداقل و حداکثر و میانگین پتانسیل عملکرد
  let potentialViolin = decades[0]?.map((itm) =>
    parseInt(itm?.performancePotential)
  );

  let minPotentialViolin;
  let maxPotentialViolin;
  let avgPotentialViolin;
  if (potentialViolin?.length > 0) {
    minPotentialViolin = Math.min(...potentialViolin);
    maxPotentialViolin = Math.max(...potentialViolin);
    avgPotentialViolin = calculateAverage(potentialViolin).toFixed(0);
  }

  let pageNumber = 0;

  return (
    <>
      {sowingDateReportQuery?.isLoading ||
      !sowingDateReportQuery?.data?.sowingDateRecommendation ? (
        <div>
          <Spin
            size="large"
            className="flex justify-center h-60 items-center !text-purple-950"
          />
          <p className="flex justify-center items-center">
            در حال آماده‌سازی گزارش
          </p>
          <span className="flex justify-center mt-10 text-sm">
            ممکن است پردازش اطلاعات شما دقایقی زمان ببرد، لطفا منتظر بمانید...
          </span>
        </div>
      ) : (
        <div className="section-container flex flex-col items-center justify-center w-[720px] overflow-clip mx-auto !bg-white">
          {/* Cover (Introduction) */}
          <div className="cover">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={headerImage}
                alt="header-image"
                width="750px"
              />

              <div className="flex flex-col justify-between items-center w-full gap-8 mt-[-90px]">
                <p
                  className="text-[#222] text-[29px] font-bold text-center !font-[B-Nazanin]"
                  style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)" }}
                >
                  کشاورزی{" "}
                  <span className="text-president !font-[B-Nazanin]">
                    مطمئن‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر، </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    دقیق‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر و </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    اقتصادی‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر</span>
                  </span>
                  <p className="text-[#000] font-semibold text-[20px] whitespace-nowrap !font-[B-Nazanin]">
                    هدف ما تصمیم‌گیری برای کشاورز نیست، کمک به تصمیم‌گیری بهتر
                    کشاورز است
                  </p>
                </p>

                <div className="flex flex-col items-center">
                  <img
                    src={sowingLogo}
                    className="w-[287px] h-[296px] !font-[B-Nazanin]"
                    alt="harvest-icon"
                  />
                  <span
                    className="text-[#5700B4] font-bold text-[40px] !font-[B-Nazanin]"
                    style={{
                      textShadow: "1px 0px 3px rgba(118, 118, 118, 0.61)",
                    }}
                  >
                    گزارش تعیین تاریخ کاشت{" "}
                  </span>
                </div>

                <img
                  src={line}
                  alt="line"
                  width="750px"
                  className="mt-[-80px] mr-0 mb-[-45px] ml-0"
                />

                <div className="flex flex-row  justify-start items-center w-full gap-5 px-4">
                  <div className="flex flex-col justify-between items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات کشاورز
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <span className="!font-[B-Nazanin]">
                          {
                            sowingDateReportQuery?.data
                              ?.sowingDateRecommendation?.farmer?.firstName
                          }{" "}
                          {
                            sowingDateReportQuery?.data
                              ?.sowingDateRecommendation?.farmer?.lastName
                          }
                        </span>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">کد کشاورزی:</span>
                          <span className="!font-sans text-[16px]">
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.farmer?.farmerCode
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            استان و شهر:
                          </span>
                          <span className="!font-[B-Nazanin]">
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.farmer?.province
                                ?.name
                            }
                            ،{" "}
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.farmer?.city?.name
                            }
                          </span>
                        </div>
                      </div>

                      <img
                        src={
                          process.env.REACT_APP_ARVAN_S3_BASE_URL +
                          sowingDateReportQuery?.data?.sowingDateRecommendation
                            ?.farmer?.avatar
                        }
                        alt="profile"
                        width="70px"
                        height="70px"
                        className="rounded-full border-[2px] border-solid border-president"
                      />
                    </div>
                  </div>
                </div>

                <img
                  src={line}
                  alt="line"
                  width="750px"
                  className="mt-[-70px] mr-0 mb-[-45px] ml-0"
                />

                <div className="flex flex-row justify-center items-end w-full gap-7 px-4">
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات زمین
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">نام قطعه:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.season?.farm?.name
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]"> استان:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.season?.farm
                                ?.province?.name
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">شهر:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.season?.farm?.name
                            }
                          </span>
                        </div>
                      </div>

                      <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                        <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                        <AgroMapFarm
                          mapId={
                            sowingDateReportQuery?.data
                              ?.sowingDateRecommendation?.season?.farm?.id
                          }
                          type="onlyShow"
                          toolbarOptions={toolbarBaseControls}
                          markerIcon={FieldMarkerIcon}
                          currentFarmPolygon={
                            sowingDateReportQuery?.data
                              ?.sowingDateRecommendation?.season?.farm?.polygon
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin] items-start">
                      اطلاعات فصل رشد
                    </span>
                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">نام محصول:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.season?.product
                                ?.persianName
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2 ">
                          <span className="!font-[B-Nazanin]">مساحت زمین</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              sowingDateReportQuery?.data
                                ?.sowingDateRecommendation?.season?.area
                            }{" "}
                            هکتار
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">فصل رشد:</span>
                          <span className="!font-[B-Nazanin]">
                            {moment(
                              `${sowingDateReportQuery?.data?.sowingDateRecommendation?.season?.growingSeason}-04-01`,
                              "YYYY-MM-DD"
                            )
                              .locale("fa")
                              .format("YYYY")}{" "}
                            {sowingDateReportQuery?.data
                              ?.sowingDateRecommendation?.season?.persianName
                              ? ` - ${sowingDateReportQuery?.data?.sowingDateRecommendation?.season?.persianName}`
                              : ""}
                          </span>
                        </div>
                      </div>

                      <img
                        src={
                          sowingDateReportQuery?.data?.sowingDateRecommendation
                            ?.season?.product?.imageUrl
                        }
                        alt="product-name"
                        width="80px"
                        height="80px"
                        className="rounded-full"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer-image"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={footerTitle}
                  alt="footer-title-image"
                />
                <span className="absolute bottom-1 left-[60px] z-[3] text-[12px] font-bold text-[#fff] tracking-[9px] text-center">
                  WWW.AGROYAAR.COM
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          {/* <!-- Page1 --> */}
          <div className="page1">
            <div className="flex flex-col w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header-image"
                  width="750px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-col justify-center items-start w-full gap-10 px-4">
                <div className="flex flex-col gap-5">
                  <span className="font-bold text-[22px] text-[#491086] !font-[B-Nazanin]">
                    تاریخ کاشت
                  </span>
                  <div className="flex flex-row gap-5 justify-center items-center ">
                    <p className="text-justify w-[400px] leading-9 !font-[B-Nazanin]">
                      شناسایی بهترین تاریخ کاشت منجر به حصول عملکرد بالاتری در
                      مقایسه با سایر تاریخ‌های کاشت می‌گردد. انتخاب تاریخ کاشت
                      مناسب یک گیاه مستلزم شناخت دقیق نیازهای اکولوژیک آن گیاه
                      در مراحل مختلف نمو و اطلاع از تغییرات دمای درازمدت و
                      کوتاه‌مدت محیط و سایر عوامل موثر موجود در منطقه بر رشد و
                      نمو گیاه است.
                    </p>
                    <img
                      src={sowingDateReport}
                      className="w-[250px] h-[200px] rounded-lg"
                      alt=""
                    />
                  </div>
                  <p className="flex flex-row mt-5 gap-1 w-[570px] justify-center my-0 mx-auto py-[1px] items-center border-2 rounded-xl border-[#a994c0] !font-[B-Nazanin] font-semibold text-[18px]">
                    بهترین تاریخ کاشت حدودی{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در این قطعه زمین شما از
                    <span className="text-[#491086] !font-[B-Nazanin] font-semibold">
                      {startDate}
                    </span>
                    تا{" "}
                    <span className="text-[#491086] !font-[B-Nazanin] font-semibold">
                      {endDate}
                    </span>{" "}
                    است.
                  </p>
                </div>

                <div>
                  <p className="!font-[B-Nazanin] text-[#333333] font-semibold text-justify px-2">
                    کشاورز عزیز تاریخ کاشت مناسب در مزرعه گندم شما، بر اساس
                    شبیه‌سازی کاشت در روزهای مختلف با استفاده از اطلاعات
                    هواشناسی ۱۰ سال گذشته و پیش بینی سال آینده ایستگاه هواشناسی
                    ثبت شده در مزرعه شما تعیین شده است.
                  </p>
                </div>

                <div className="flex flex-col">
                  <div className="flex flex-col gap-5">
                    <span className="font-bold text-[22px] text-[#491086] !font-[B-Nazanin]">
                      چه محاسباتی برای تعیین تاریخ کاشت انجام شده است؟
                    </span>

                    <div className="flex flex-col gap-4 text-[15px] !font-[B-Nazanin]">
                      <p className="!font-[B-Nazanin]">
                        1. بررسی اطلاعات اقلیمی 10 ساله این قطعه زمین شما
                      </p>
                      <p className="!font-[B-Nazanin]">
                        2. پیش بینی آب و هوای یک سال آینده در طول فصل رشد{" "}
                        <span className="!font-[B-Nazanin]">
                          {
                            sowingDateReportQuery?.data
                              ?.sowingDateRecommendation?.season?.product
                              ?.persianName
                          }
                        </span>
                        در این قطعه زمین
                      </p>
                      <p className="!font-[B-Nazanin]">
                        3. تحلیل و بررسی خصوصیات خاک قطعه زمین شما
                      </p>
                      <p className="!font-[B-Nazanin]">
                        4. تحلیل رشد{" "}
                        <span className="!font-[B-Nazanin]">
                          {
                            sowingDateReportQuery?.data
                              ?.sowingDateRecommendation?.season?.product
                              ?.persianName
                          }
                        </span>{" "}
                        شما در این قطعه زمین و بررسی تنش‌های احتمالی در تمام
                        مراحل رشدی
                      </p>
                      <p className="!font-[B-Nazanin]">
                        5. محاسبه نیاز آبی گیاه شما و تعیین بهترین تاریخ کاشت به
                        منظور کمترین خسارت وارده در مراحل حساس به آب
                      </p>
                      <p className="!font-[B-Nazanin]">
                        6. محاسبه پتانسیل عملکرد قطعه زمین شما و تعیین بهترین
                        تاریخ کاشت برای دستیابی به بیشترین عملکرد
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
              <img
                className="absolute bottom-0 z-[2]"
                src={footerImage}
                width="750px"
                alt="footer-image"
              />
              <img
                className="absolute bottom-2 left-[30px] z-[3]"
                src={agroyaarPlatform}
                alt="footer-image"
              />
              <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                کد گزارش:{" "}
                {
                  sowingDateReportQuery?.data?.sowingDateRecommendation
                    ?.sowingDateRecommendationCode
                }
              </span>
              <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                {++pageNumber}
              </span>
            </div>
          </div>

          {/* <!-- Page2 --> */}
          <div className="page2">
            <div className="flex flex-col w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header-image"
                  width="750px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-col justify-center items-start gap-20 w-full px-4">
                <div className="flex flex-col gap-2">
                  <span className="font-black text-[22px] text-[#222] !font-[B-Nazanin]">
                    طول دوره رشد مزرعه شما
                  </span>
                  <p className="text-justify leading-9 text-[15px] !font-[B-Nazanin]">
                    نمودار زیر طول دوره رشد مزرعه شما در تاریخ کاشت‌های مختلف را
                    نشان می‌دهد. این نمودار نشان می‌دهد که اگر مزرعه{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    خود را در هر تاریخی کاشت نمائید حدودا در چه تاریخی برداشت
                    خواهید کرد و دوره رشد مزرعه شما چند روز خواهد بود. به عنوان
                    مثال اگر کاشت{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در مزرعه شما در تاریخ{" "}
                    {moment(ranges[0]?.Start)?.format("jYYYY/jMM/jDD")} انجام
                    شود، تاریخ برداشت احتمالی حدودا{" "}
                    {moment(ranges[0]?.End)?.format("jYYYY/jMM/jDD")} و طول دوره
                    رشد در مزرعه{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    شما {diffInDays} روز خواهد بود.{" "}
                  </p>
                </div>

                <GanttChart
                  serverData={sowingDateReportQuery?.data}
                  ranges={ranges}
                  setRanges={setRanges}
                  diffInDays={diffInDays}
                />
              </div>
            </div>

            <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
              <img
                className="absolute bottom-0 z-[2]"
                src={footerImage}
                width="750px"
                alt="footer-image"
              />
              <img
                className="absolute bottom-2 left-[30px] z-[3]"
                src={agroyaarPlatform}
                alt="footer-image"
              />
              <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                کد گزارش:{" "}
                {
                  sowingDateReportQuery?.data?.sowingDateRecommendation
                    ?.sowingDateRecommendationCode
                }
              </span>
              <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                {++pageNumber}
              </span>
            </div>
          </div>

          {/* <!-- Page3 --> */}
          <div className="page3">
            <div className="flex flex-col w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header-image"
                  width="750px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-col justify-center items-start w-full px-4 -mt-3">
                <div className="flex flex-col w-full">
                  <span className="font-black text-[21px] text-[#222] !font-[B-Nazanin]">
                    وضعیت آب و هوای سال گذشته و پیش بینی سال آینده{" "}
                  </span>
                  <span className="text-[18px] text-[#222] font-semibold !font-[B-Nazanin]">
                    آب و هوای فصل رشد گذشته :
                  </span>
                  <p className="text-justify leading-6 text-[15px] !font-[B-Nazanin]">
                    نمودار زیر تغییرات دمای حداقل، دمای حداکثر و بارش سال گذشته
                    را در طی دوره رشد محصول{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در مزرعه شما نشان می‌دهد. متوسط دمای حداقل و دمای حداکثر در
                    طی فصل رشد{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در سال گذشته به ترتیب{" "}
                    <span className="!font-[B-Nazanin] font-semibold">
                      {calculateAverage(minTemperatureHistory).toFixed(0)}
                    </span>{" "}
                    و{" "}
                    <span className="!font-[B-Nazanin] font-semibold">
                      {calculateAverage(maxTemperatureHistory).toFixed(0)}
                    </span>{" "}
                    درجه سانتیگراد بوده است. همچنین، مجموع بارش در طی فصل رشد{" "}
                    <span className="!font-[B-Nazanin] font-semibold">
                      {totalPrecipitationHistory}
                    </span>{" "}
                    میلیمتر بوده است.
                  </p>

                  <WeatherChart
                    serverData={sowingDateReportQuery?.data}
                    charType="weatherHistory"
                  />
                </div>

                <div className="flex flex-col">
                  <span className="text-[18px] font-semibold text-[#222] !font-[B-Nazanin]">
                    پیش‌بینی آب و هوای فصل رشد آینده :
                  </span>

                  <p className="text-justify leading-6 text-[15px] !font-[B-Nazanin]">
                    نمودار زیر پیش‌بینی تغییرات دمای حداقل، دمای حداکثر و بارش
                    سال آینده را در طی دوره رشد محصول{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    شما نشان می‌دهد. پیش‌بینی می‌شود متوسط دمای حداقل و دمای
                    حداکثر در طی فصل رشد{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در سال آینده به ترتیب{" "}
                    <span className="!font-[B-Nazanin] font-semibold">
                      {calculateAverage(minTemperatureFuture).toFixed(0)}
                    </span>{" "}
                    و{" "}
                    <span className="!font-[B-Nazanin] font-semibold">
                      {calculateAverage(maxTemperatureFuture).toFixed(0)}
                    </span>{" "}
                    درجه سانتیگراد و مجموع بارش در طی فصل رشد{" "}
                    <span className="!font-[B-Nazanin] font-semibold">
                      {totalPrecipitationFuture}
                    </span>{" "}
                    میلیمتر باشد.
                  </p>

                  <WeatherChart
                    serverData={sowingDateReportQuery?.data}
                    charType="weatherFuture"
                  />
                </div>
              </div>
            </div>

            <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
              <img
                className="absolute bottom-0 z-[2]"
                src={footerImage}
                width="750px"
                alt="footer-image"
              />
              <img
                className="absolute bottom-2 left-[30px] z-[3]"
                src={agroyaarPlatform}
                alt="footer-image"
              />
              <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                کد گزارش:{" "}
                {
                  sowingDateReportQuery?.data?.sowingDateRecommendation
                    ?.sowingDateRecommendationCode
                }
              </span>
              <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                {++pageNumber}
              </span>
            </div>
          </div>

          {/* <!-- Page4 --> */}
          <div className="page4">
            <div className="flex flex-col w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header-image"
                  width="750px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-col justify-center gap-3 items-start w-full px-4">
                <div className="flex flex-col gap-2">
                  <span className="font-black text-[22px] text-[#222] !font-[B-Nazanin]">
                    تعیین بهترین تاریخ کاشت{" "}
                  </span>
                  <p className="text-justify leading-9 text-[15px] !font-[B-Nazanin]">
                    شکل زیر پتانسیل عملکرد{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در مزرعه شما را در تاریخ کاشت‌های مختلف نشان می‌دهد. بر این
                    اساس بهترین تاریخ کاشت احتمالی{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در مزرعه شما در سال جاری از {startDate} تا {endDate}{" "}
                    می‌باشد. پتانسیل عملکرد مزرعه شما در محدوده تاریخ کاشت بهینه
                    حدود {maxAvg} کیلوگرم در هکتار برآورد شد.
                  </p>
                </div>
                <div className="flex flex-col gap-12">
                  <p className="text-justify leading-9 text-[15px] !font-[B-Nazanin]">
                    <span className="font-bold text-[17px] text-red-500 !font-[B-Nazanin]">
                      نکته مهم:{" "}
                      <span className="text-justify text-black font-normal leading-9 text-[15px] !font-[B-Nazanin]">
                        پتانسیل عملکرد در واقع بالاترین عملکردی که با مدیریت
                        مطلوب تمام عوامل قابل مدیریت در مزرعه می‌توانیم به دست
                        آوریم بوده و متفاوت از عملکردی است که با مدیریت فعلی در
                        مزرعه شما کسب می‌گردد. لذا برای رسیدن به پتانسیل عملکرد
                        علاوه بر تاریخ کاشت باید سایر عوامل مدیریتی نیز اصلاح
                        شوند. میتوانید از سامانه خلاء عملکرد اگرویار برای این
                        منظور استفاده نمائید.
                      </span>
                    </span>
                  </p>
                  <ScatterPlotChart
                    serverData={sowingDateReportQuery?.data}
                    chartType="potential"
                    startDate={startDate}
                    endDate={endDate}
                  />
                </div>
                <p className="flex flex-row justify-center w-full pt-10 text-[17px] !font-[B-Nazanin] text-[#333333] font-semibold">
                  بهترین تاریخ کاشت محدوده ای از نمودار بالا است که پیش‌بینی
                  بیشترین پتانسیل عملکرد شده است.
                </p>
              </div>
            </div>

            <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
              <img
                className="absolute bottom-0 z-[2]"
                src={footerImage}
                width="750px"
                alt="footer-image"
              />
              <img
                className="absolute bottom-2 left-[30px] z-[3]"
                src={agroyaarPlatform}
                alt="footer-image"
              />
              <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                کد گزارش:{" "}
                {
                  sowingDateReportQuery?.data?.sowingDateRecommendation
                    ?.sowingDateRecommendationCode
                }
              </span>
              <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                {++pageNumber}
              </span>
            </div>
          </div>

          {/* <!-- Page5 --> */}
          <div className="page5">
            <div className="flex flex-col w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header-image"
                  width="750px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-col justify-center gap-20 items-start w-full px-4">
                <div className="flex flex-col gap-2">
                  <span className="font-black text-[22px] text-[#222] !font-[B-Nazanin]">
                    بررسی بهترین تاریخ کاشت در 10 سال گذشته{" "}
                  </span>
                  <p className="text-justify leading-9 text-[15px] !font-[B-Nazanin]">
                    شکل زیر تغییرات پتانسیل عملکرد{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در مزرعه شما را در تاریخ کاشت‌های مختلف در بازه زمانی 10 سال
                    گذشته نشان می‌دهد. هر کدام از دو راس بالا و پایین نمودار،
                    توزیع عملکرد را برای هر تاریخ کاشت نشان می‌دهد. بخش‌های
                    عریض‌تر نمودار نشان‌دهنده این است که آن محدوده عملکردی، با
                    احتمال بیشتری می‌توانند روی دهد و هر چه این عرض کوچکتر باشد
                    احتمال آن کمتر است. به عنوان مثال در{" "}
                    {convertDecadesViolinText(firstDate)} بیشترین و کمترین
                    پتانسیل عملکرد به ترتیب {minPotentialViolin} و{" "}
                    {maxPotentialViolin} کیلوگرم در هکتار بوده و میانگین پتانسیل
                    عملکرد در این تاریخ کاشت حدود {avgPotentialViolin} کیلوگرم
                    در هکتار است.
                  </p>
                </div>

                <div className="flex flex-col gap-14">
                  {/* <p className="flex flex-row justify-center w-full !font-[B-Nazanin] text-[#333333] font-semibold">
                    محدوده زمانی مطلوب کاشت{" "}
                    <span className="!font-[B-Nazanin] font-semibold">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    مزرعه شما در 10 سال گذشته{" "}
                    <span className="text-president">........</span> بوده است.
                  </p> */}

                  <ViolinPlot serverData={sowingDateReportQuery?.data} />
                </div>
              </div>
            </div>

            <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
              <img
                className="absolute bottom-0 z-[2]"
                src={footerImage}
                width="750px"
                alt="footer-image"
              />
              <img
                className="absolute bottom-2 left-[30px] z-[3]"
                src={agroyaarPlatform}
                alt="footer-image"
              />
              <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                کد گزارش:{" "}
                {
                  sowingDateReportQuery?.data?.sowingDateRecommendation
                    ?.sowingDateRecommendationCode
                }
              </span>
              <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                {++pageNumber}
              </span>
            </div>
          </div>

          {/* <!-- Page6 --> */}
          <div className="page6">
            <div className="flex flex-col w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header-image"
                  width="750px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-col justify-center gap-3 items-start w-full px-4">
                <div className="flex flex-col gap-2">
                  <span className="font-black text-[22px] text-[#222] !font-[B-Nazanin]">
                    بهره‌وری آبیاری در تاریخ کاشت‌های مختلف{" "}
                  </span>
                  <p className="text-justify leading-9 text-[15px] !font-[B-Nazanin]">
                    شکل زیر بهره‌وری آبیاری{" "}
                    <span className="!font-[B-Nazanin]">
                      {
                        sowingDateReportQuery?.data?.sowingDateRecommendation
                          ?.season?.product?.persianName
                      }
                    </span>{" "}
                    در مزرعه شما را در تاریخ کاشت‌های مختلف نشان می‌دهد. مقدار
                    <span className="!font-[B-Nazanin] font-bold"> 100</span> به
                    معنی بالاترین بهره‌وری آبیاری است.
                  </p>
                  <div className="py-16">
                    <ScatterPlotChart
                      serverData={sowingDateReportQuery?.data}
                      chartType="irrigation"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
              <img
                className="absolute bottom-0 z-[2]"
                src={footerImage}
                width="750px"
                alt="footer-image"
              />
              <img
                className="absolute bottom-2 left-[30px] z-[3]"
                src={agroyaarPlatform}
                alt="footer-image"
              />
              <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                {++pageNumber}
              </span>
            </div>
          </div>

          <div className="flex flex-col">
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[20px] right-[100px] max-lg:right-[32px] px-8 text-[18px]  w-[150px]"
              id="download-button"
              onClick={() => window.print()}
            >
              <p>پرینت گزارش</p>
            </Button>
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[65px] right-[100px] max-lg:right-[32px] px-8 text-[18px] w-[150px]"
              id="download-button"
              onClick={() => window.close()}
            >
              <p> بازگشت</p>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default SowingDateRecommendationReport;
